import * as React from 'react';
import { PlayIcon } from '../icons/PlayIcon';
import { PauseIcon } from '../icons/PauseIcon';
import { CrossedSpeakerIcon } from '../icons/CrossedSpeakerIcon';
import * as styles from './LocalVideoWithControls.module.scss';
import clsx from 'clsx';

const isImageSource = (src?: string): boolean => {
  if (!src) return false;

  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
  return imageExtensions.some((ext) => src.toLowerCase().endsWith(ext));
};

export const LocalVideoWithControls = ({
  src,
  activeItem,
  className,
  videoClassName,
  isMuteDisabled = false,
  isPlayDisabled = false,
}: {
  src: string;
  activeItem?: string | number;
  className?: string;
  videoClassName?: string;
  isMuteDisabled?: boolean;
  isPlayDisabled?: boolean;
}) => {
  const [isPaused, setIsPaused] = React.useState(false);
  const [isMuted, setIsMuted] = React.useState(true);
  const [hasAudio, setHasAudio] = React.useState(false);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const isImage = isImageSource(src);

  React.useEffect(() => {
    const videoElement = videoRef.current;

    const handleLoadedData = () => {
      if (videoElement) {
        const video = videoElement as any;
        setHasAudio(
          !!video.mozHasAudio ||
            !!video.webkitAudioDecodedByteCount ||
            !!(video.audioTracks && video.audioTracks.length),
        );
      }
    };

    if (videoElement) {
      videoElement.addEventListener('loadeddata', handleLoadedData);
    }

    return () => {
      if (videoElement) {
        videoElement.removeEventListener('loadeddata', handleLoadedData);
      }
    };
  }, []);

  const handleMuteClick = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handlePlayPauseClick = () => {
    if (videoRef.current) {
      if (isPaused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
      setIsPaused(!isPaused);
    }
  };

  return (
    <div className={clsx(styles.video, className)} role="region" aria-label="Video player">
      {/* overlay to make video scrollable horizontally in other components */}
      <div className={styles.video__overlay} onClick={handleMuteClick} aria-hidden="true" />
      <video
        key={activeItem || src}
        ref={videoRef}
        autoPlay
        muted={isMuted}
        playsInline
        loop
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        className={videoClassName}
        aria-label="Video content"
      >
        <source src={src} type="video/mp4" />
      </video>
      {!isMuteDisabled && hasAudio && (
        <button
          aria-label={isMuted ? 'Unmute video' : 'Mute video'}
          aria-pressed={!isMuted}
          className={styles.video__muteControl}
          onClick={handleMuteClick}
        >
          {isMuted ? <CrossedSpeakerIcon /> : null}
        </button>
      )}
      {!isPlayDisabled && !isImage && (
        <button
          className={styles.video__playControl}
          onClick={handlePlayPauseClick}
          aria-label={isPaused ? 'Play video' : 'Pause video'}
          aria-pressed={!isPaused}
        >
          {isPaused ? <PlayIcon aria-hidden="true" /> : <PauseIcon aria-hidden="true" />}
        </button>
      )}
    </div>
  );
};
